// src/components/personal/sections/ClientsSection/hooks/useClientsData.ts
import { useMemo, useCallback, useEffect } from 'react';
import { Client, ClientFilters, CacheInfo } from '../types/types';
import { useClientDataCore } from './useClientDataCore';
import { useClientPagination } from './useClientPagination';
import { useClientFilters } from './useClientFilters';
import { useClientSearch } from './useClientSearch';
import { useClientSorting } from './useClientSorting';

// Объявление глобальных переменных для TypeScript
declare global {
  interface Window {
    __ACTIVE_FILTERS_CHECK: () => boolean;
    __FILTERS_CHANGED_CALLBACK: ((isFiltering: boolean) => void) | null;
    __BLOCK_CLIENT_PAGE_LOAD: boolean;
  }
}

// Инициализация глобальных переменных
if (typeof window !== 'undefined') {
  window.__ACTIVE_FILTERS_CHECK = () => false;
  window.__FILTERS_CHANGED_CALLBACK = null;
  window.__BLOCK_CLIENT_PAGE_LOAD = false;
}

// Интерфейс для параметров хука
export interface UseClientsDataProps {
  initialClients?: Client[];
}

// Интерфейс для возвращаемого значения хука
export interface UseClientsDataReturn {
  // Данные клиентов
  allClients: Client[];
  filteredClients: Client[];
  displayedClients: Client[];
  
  // Состояние загрузки
  isLoading: boolean;
  error: string | null;
  allClientsLoaded: boolean;
  totalClientsCount: number;
  
  // Методы для загрузки данных
  loadAllClients: () => Promise<void>;
  loadPage: (page: number, size?: number) => Promise<void>;
  refreshClients: (forceRefresh?: boolean) => Promise<void>;
  exportClientsToCSV: (filteredClients: Client[]) => Promise<boolean>;
  
  // Фильтрация
  filters: ClientFilters;
  searchQuery: string;
  showFilters: boolean;
  updateFilters: (newFilters: Partial<ClientFilters>) => void;
  setSearchQuery: (query: string) => void;
  toggleFilters: () => void;
  isFiltering: boolean;
  resetFilters: () => void;
  clearCache: () => void;
  
  // Пагинация
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // Сортировка
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  setSorting: (column: string, direction: 'asc' | 'desc') => void;
  
  // Дополнительные данные
  cacheInfo: CacheInfo | null;
}

// Функция для поддержки обоих вариантов вызова
export function useClientData(props: UseClientsDataProps | Client[] = []): UseClientsDataReturn {
  // Определяем, какой формат аргументов использован
  const initialClients = Array.isArray(props) ? props : props.initialClients || [];
  
  return useClientsDataImplementation({ initialClients });
}

// Основная реализация хука
export function useClientsDataImplementation({ 
  initialClients = [] 
}: UseClientsDataProps): UseClientsDataReturn {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Используем базовый хук для загрузки данных
  const {
    clients: coreClients,
    isLoading,
    error,
    allClientsLoaded,
    totalClientsCount,
    loadAllClients: coreLoadAllClients,
    loadClientPage,
    refreshClients: coreRefreshClients,
    exportClientsToCSV,
    clearCache,
    cacheInfo
  } = useClientDataCore(initialClients);
  
  // Используем хук для фильтрации
  const {
    filters,
    filteredClients: clientsAfterFilters,
    isFiltering,
    showFilters,
    updateFilters,
    resetFilters,
    toggleFilters
  } = useClientFilters({
    clients: coreClients
  });
  
  // Используем хук для поиска
  const {
    searchQuery,
    searchResults: clientsAfterSearch,
    setSearchQuery
  } = useClientSearch({
    clients: clientsAfterFilters
  });
  
  // Установка функции проверки активных фильтров для использования в других компонентах
  useEffect(() => {
    window.__ACTIVE_FILTERS_CHECK = () => isFiltering || searchQuery.trim() !== '';
    return () => {
      window.__ACTIVE_FILTERS_CHECK = () => false;
    };
  }, [isFiltering, searchQuery]);
  
  // В useClientDataCore добавьте подписку на изменение фильтров
  useEffect(() => {
    // Сохраняем предыдущее значение переменной
    const prevCallback = window.__FILTERS_CHANGED_CALLBACK;
    
    // Устанавливаем нашу функцию
    window.__FILTERS_CHANGED_CALLBACK = (isFiltering) => {
      if (isFiltering) {
        // Если фильтры активны, блокируем загрузку страниц
        window.__BLOCK_CLIENT_PAGE_LOAD = true;
      } else {
        window.__BLOCK_CLIENT_PAGE_LOAD = false;
      }
    };
    
    return () => {
      // Восстанавливаем предыдущее значение при размонтировании
      window.__FILTERS_CHANGED_CALLBACK = prevCallback;
    };
  }, []);
  
  // Вычисляем общий список с применением фильтров и поиска
  const processedClients = useMemo(() => {
    // Если есть строка поиска, применяем поиск к отфильтрованным данным
    if (searchQuery) {
      if (DEBUG) console.log(`Применение поиска "${searchQuery}" к ${clientsAfterFilters.length} клиентам`);
      return clientsAfterSearch;
    }
    
    // Иначе возвращаем только отфильтрованные данные
    return clientsAfterFilters;
  }, [clientsAfterFilters, clientsAfterSearch, searchQuery, DEBUG]);
  
  // Используем хук для сортировки
  const {
    sortColumn,
    sortDirection,
    sortedClients,
    setSorting
  } = useClientSorting({
    clients: processedClients
  });
  
  // Используем хук для пагинации
  const {
    pagination,
    setCurrentPage,
    setPageSize,
    startIndex,
    endIndex
  } = useClientPagination({
    totalItems: sortedClients.length
  });
  
  // Вычисляем список отображаемых клиентов (для текущей страницы)
  const displayedClients = useMemo(() => {
    return sortedClients.slice(startIndex, endIndex);
  }, [sortedClients, startIndex, endIndex]);
  
  // Адаптеры методов для обратной совместимости
  
  // Метод загрузки всех клиентов
  const loadAllClients = useCallback(async () => {
    if (DEBUG) console.log('loadAllClients - запуск');
    // Всегда загружаем всех клиентов при вызове этого метода
    await coreLoadAllClients();
  }, [coreLoadAllClients, DEBUG]);
  
  // Метод загрузки страницы (исправленная версия)
  const loadPage = useCallback(async (page: number, size: number = pagination.pageSize) => {
    if (DEBUG) console.log(`loadPage - страница ${page}, размер ${size}, фильтрация: ${isFiltering}, поиск: ${searchQuery}`);
    
    // Принудительно блокируем загрузку страницы, если активны фильтры или поиск
    if (isFiltering || searchQuery.trim() !== '') {
      if (DEBUG) console.log('loadPage - БЛОКИРОВКА загрузки страницы при активных фильтрах, используем кешированные данные');
      
      // Только устанавливаем параметры пагинации, без загрузки данных
      setCurrentPage(page);
      setPageSize(size);
      return;
    }
    
    // Иначе стандартное поведение - загружаем указанную страницу
    const offset = (page - 1) * size;
    await loadClientPage(offset, size);
    setCurrentPage(page);
    setPageSize(size);
  }, [loadClientPage, setCurrentPage, setPageSize, pagination.pageSize, isFiltering, searchQuery, DEBUG]);
  
  // Метод обновления клиентов
  const refreshClients = useCallback(async (forceRefresh: boolean = false) => {
    if (DEBUG) console.log(`refreshClients - forceRefresh: ${forceRefresh}, фильтрация: ${isFiltering}, поиск: ${searchQuery}`);
    
    // Всегда загружаем всех клиентов, если активна фильтрация или поиск
    const needAllClients = isFiltering || searchQuery.trim() !== '';
    
    if (needAllClients) {
      if (DEBUG) console.log('refreshClients - принудительная загрузка всех клиентов из-за активных фильтров');
      await coreRefreshClients(forceRefresh, true); // Второй аргумент - принудительная загрузка всех клиентов
    } else {
      await coreRefreshClients(forceRefresh, false);
    }
    
    // Сбрасываем пагинацию на первую страницу при обновлении
    if (forceRefresh) {
      setCurrentPage(1);
    }
  }, [coreRefreshClients, setCurrentPage, isFiltering, searchQuery, DEBUG]);
  
  return {
    // Данные о клиентах
    allClients: coreClients,
    filteredClients: sortedClients, // Все клиенты после фильтрации, поиска и сортировки
    displayedClients, // Клиенты на текущей странице
    
    // Состояние загрузки
    isLoading,
    error,
    allClientsLoaded,
    totalClientsCount,
    
    // Методы для загрузки данных
    loadAllClients,
    loadPage,
    refreshClients,
    exportClientsToCSV,
    
    // Фильтрация и поиск
    filters,
    searchQuery,
    showFilters,
    updateFilters,
    setSearchQuery,
    toggleFilters,
    isFiltering,
    resetFilters,
    clearCache,
    
    // Пагинация
    pagination,
    setCurrentPage,
    setPageSize,
    
    // Сортировка
    sortColumn,
    sortDirection,
    setSorting,
    
    // Дополнительная информация
    cacheInfo
  };
}

// Для обратной совместимости
export const useClientsData = useClientData;